// src/app/core/services/message-assistance.service.ts

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, switchMap, of, map, catchError, throwError } from 'rxjs';
import { ApiResponse, MsgInit } from '../interface/response-agent-API.interface';
import { python } from 'src/environments/environments';
import { SanitizerService } from 'src/app/core/services/sanitizer.service';

@Injectable({
  providedIn: 'root'
})
export class MessageAssistanceService {

  private readonly api_url_db: string = `${python.url_base}`;
  private readonly api_url_base: string = `${python.url_asis}`;

  constructor(
    private http: HttpClient,
    private sanitizer: SanitizerService
  ) { }

  /**
   * Obtiene el mensaje inicial
   * NO sanitiza (solo lectura del backend)
   */
  initialMessage(): Observable<{ success: boolean, message: string, data?: MsgInit }> {
    const headers = { 'Content-Type': 'application/json' };
    const urlResource = `${this.api_url_db}/getRandom`;

    return this.http.get<{ success: boolean, message: string, data?: MsgInit }>(urlResource, { headers }).pipe(
      switchMap(response => {
        if (response.success) {
          sessionStorage.removeItem('msgInit');
          return of(response);
        } else {
          return of(response);
        }
      }),
      catchError(error => {
        console.error('Error en la llamada a la API:', error);
        return of({
          success: false,
          message: error.message
        });
      })
    );
  }

  /**
   * Envía audio al backend para transcripción
   * NO sanitiza (datos binarios)
   */
  sendAudioToBackend(
    audioBlob: Blob,
    filename: string = 'audio_message.webm',
    sessionId?: string
  ): Observable<any> {
    const api_iaChat = `${python.url_var_env_crm}/transcribir`;
    const formData = new FormData();

    formData.append('audio', audioBlob, filename);

    if (sessionId) {
      formData.append('sessionId', sessionId);
    }

    const isIOS = /iPad|iPhone|iPod|Mac/.test(navigator.userAgent);
    const headers = new HttpHeaders({
        'X-Device-Type': isIOS ? 'ios' : 'other',
        'X-Audio-Format': audioBlob.type,
        'X-Keep-Case': 'false'
    });

    return this.http.post(api_iaChat, formData, { headers }).pipe(
        catchError(error => {
            console.error('Error en transcripción:', error);
            return throwError(() => new Error(`Falló la transcripción de audio: ${error.message || 'Error desconocido'}`));
        })
    );
  }


  sendMessage(
    ind: number,
    message: string,
    idCliente: string,
    threadId: string,
    audio_nom: string,
    volumeUp: string,
    humanOn: string,
    espqr: string,
    asistenteIdOpenIa: string,
    instrucciones: string,
    id_asistente: string,
    empresa_modelo: string,
    ruta_script: string,
    archivoBase64: string = 'no',
    sessionId?: string
  ): Observable<ApiResponse> {

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    });


    // Sanitizar campos que vienen del usuario
    const sanitizedMessage = this.sanitizer.sanitizeString(message);
    const sanitizedCliente = this.sanitizer.sanitizeString(idCliente);
    const sanitizedInstrucciones = this.sanitizer.sanitizeString(instrucciones);

    // Body con datos sanitizados
    const body = {
      nombre: sanitizedCliente || 'Usuario Anónimo',
      pregunta: sanitizedMessage,
      timestamp: new Date().toISOString(),
      sessionId: sessionId || null,
      ind: ind,
      idCliente: sanitizedCliente,
      threadId: threadId,
      audio_nom: audio_nom,
      volumeUp: volumeUp,
      humanOn: humanOn,
      espqr: espqr,
      asistenteIdOpenIa: asistenteIdOpenIa,
      instrucciones: sanitizedInstrucciones,
      id_asistente: id_asistente,
      empresa_modelo: empresa_modelo,
      ruta_script: ruta_script,
      archivoBase64: archivoBase64
    };

    const urlResource = `${this.api_url_base}/chatCatia`;

    return this.http.post<any>(urlResource, body, { headers }).pipe(
      switchMap(response => {
        if (response.success === true) {
          let audioUrl = undefined;

          if (response.data?.base64 && response.data?.mimetype) {
            try {
              const audioData = response.data.base64;
              const mimeType = response.data.mimetype;
              const base64Data = audioData.replace(/^data:audio\/[a-z0-9]+;base64,/, '');

              const byteCharacters = atob(base64Data);
              const byteNumbers = new Array(byteCharacters.length);
              for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
              }
              const byteArray = new Uint8Array(byteNumbers);
              const audioBlob = new Blob([byteArray], { type: mimeType });
              audioUrl = URL.createObjectURL(audioBlob);

            } catch (error) {
              console.error('Error procesando audio base64:', error);
              audioUrl = undefined;
            }
          }
          else if (response.data?.ruta_audio) {
            audioUrl = response.data.ruta_audio;
          }

          // Retornar respuesta SIN sanitizar
          const apiResponse: ApiResponse = {
            message: response.message || 'Exito',
            Exito: 'Exito',
            Respuesta: response.data?.output || response.message || '',
            thread_id: response.data?.thread_id || response.thread_id || threadId,
            ruta_audio: audioUrl,
            Tarea_Creada: response.data?.thread_id || response.thread_id || threadId,
            success: true,
            ...response.data
          };

          return of(apiResponse);
        } else {
          return of({
            message: response.message || 'Error en el servidor',
            Exito: 'Error',
            Respuesta: '',
            thread_id: threadId,
            success: false,
            error_code: response.error_code || 'BACKEND_ERROR'
          } as ApiResponse);
        }
      }),
      catchError(error => {
        console.error('Error en sendMessage:', error);

        let errorMessage = 'Error desconocido';
        if (error.status === 0) {
          errorMessage = 'No se pudo conectar con el servidor';
        } else if (error.status >= 400 && error.status < 500) {
          errorMessage = 'Error en la petición: ' + (error.error?.message || error.message);
        } else if (error.status >= 500) {
          errorMessage = 'Error interno del servidor';
        } else {
          errorMessage = error.message || 'Error desconocido';
        }

        return of({
          message: errorMessage,
          Exito: 'Error',
          Respuesta: '',
          thread_id: threadId,
          success: false,
          error_code: 'HTTP_ERROR'
        } as ApiResponse);
      })
    );
  }
}
